import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, Heart, Play, UserPlus, UserMinus } from 'lucide-react';
import { fileUrl, formatViews, timeAgoCa } from '../lib/api';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export function VideoCard({ video, compact = false }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const [showPreview, setShowPreview] = useState(false);
    const [liked, setLiked] = useState(video.liked || false);
    const [likes, setLikes] = useState(video.likes || 0);
    const [likeBusy, setLikeBusy] = useState(false);
    const [following, setFollowing] = useState(video.is_following || false);
    const [followBusy, setFollowBusy] = useState(false);
    const enterTimerRef = useRef(null);

    useEffect(() => {
        setLiked(video.liked || false);
        setLikes(video.likes || 0);
        setFollowing(video.is_following || false);
    }, [video.video_id, video.liked, video.likes, video.is_following]);

    const supportsHover = typeof window !== 'undefined' && window.matchMedia('(hover:hover) and (pointer:fine)').matches;

    const startPreview = () => {
        setShowPreview(true);
        const el = videoRef.current;
        if (el) {
            el.muted = true;
            el.playsInline = true;
            el.play().catch(() => {});
        }
    };

    const handleEnter = () => {
        if (!supportsHover) return;
        if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
        enterTimerRef.current = setTimeout(startPreview, 280);
    };
    const handleLeave = () => {
        if (enterTimerRef.current) clearTimeout(enterTimerRef.current);
        setShowPreview(false);
        const el = videoRef.current;
        if (el) {
            el.pause();
            try { el.currentTime = 0; } catch {}
        }
    };

    useEffect(() => () => { if (enterTimerRef.current) clearTimeout(enterTimerRef.current); }, []);

    // Touch devices: first tap shows the muted preview, second tap opens the video.
    const handleTouchTap = (e) => {
        if (supportsHover) return;
        if (showPreview) return;
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('alb-stop-previews', { detail: video.video_id }));
        startPreview();
    };

    // Stop this preview when another card starts one (mobile).
    useEffect(() => {
        const onStop = (e) => {
            if (e.detail === video.video_id) return;
            setShowPreview(false);
            const el = videoRef.current;
            if (el) {
                el.pause();
                try { el.currentTime = 0; } catch {}
            }
        };
        window.addEventListener('alb-stop-previews', onStop);
        return () => window.removeEventListener('alb-stop-previews', onStop);
    }, [video.video_id]);

    const toggleLike = async () => {
        if (!user) {
            toast.error("Inicia sessió per donar M'agrada");
            navigate('/entrar');
            return;
        }
        if (likeBusy) return;
        setLikeBusy(true);
        try {
            const response = await api.post(`/videos/${video.video_id}/like`);
            setLiked(response.data.liked);
            setLikes((count) => Math.max(0, count + (response.data.liked ? 1 : -1)));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut actualitzar el M’agrada');
        } finally {
            setLikeBusy(false);
        }
    };

    const toggleFollow = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!user) {
            toast.error('Inicia sessió per seguir creadors');
            navigate('/entrar');
            return;
        }
        if (followBusy) return;
        setFollowBusy(true);
        try {
            if (following) {
                await api.delete(`/users/${video.uploader_id}/follow`);
                setFollowing(false);
            } else {
                await api.post(`/users/${video.uploader_id}/follow`);
                setFollowing(true);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut actualitzar el seguiment');
        } finally {
            setFollowBusy(false);
        }
    };

    const thumb = video.thumbnail_url ? fileUrl(video.thumbnail_url) : null;
    const src = fileUrl(video.video_url);

    return (
        <article
            data-testid="video-card"
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            className="group block overflow-hidden rounded-[14px] border border-[rgba(245,233,216,0.08)] hover:border-[rgba(245,233,216,0.16)] bg-transparent transition-colors"
        >
            <Link to={`/video/${video.video_id}`} onClick={handleTouchTap} data-testid="video-card-thumbnail" className="relative block aspect-video w-full overflow-hidden bg-[color:var(--alb-surface)]">
                {thumb ? (
                    <img src={thumb} alt={video.title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                ) : (
                    <video src={src} preload="metadata" muted className="absolute inset-0 h-full w-full object-cover" />
                )}
                <video
                    ref={videoRef}
                    src={src}
                    muted
                    playsInline
                    preload="none"
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ${showPreview ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    data-testid="video-hover-preview"
                />
                {!showPreview && (
                    <div className="absolute inset-0 grid place-items-center bg-gradient-to-b from-transparent via-transparent to-black/60">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity h-10 w-10 rounded-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] grid place-items-center shadow-lg"><Play className="h-5 w-5 fill-current" /></div>
                    </div>
                )}
                {video.is_short && (
                    <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider font-semibold bg-[color:var(--alb-pink)] text-white px-2 py-0.5 rounded-full">Short</span>
                )}
                {showPreview && (
                    <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider font-semibold bg-black/70 text-[color:var(--alb-text)] px-2 py-0.5 rounded-full">Preview</span>
                )}
            </Link>
            <div className={`px-2 pt-3 pb-1 ${compact ? '' : 'space-y-1'}`}>
                <Link to={`/video/${video.video_id}`} className="block hover:underline">
                    <h3 data-testid="video-card-title" className="font-display font-semibold text-[15px] leading-snug line-clamp-2 text-[color:var(--alb-text)]">{video.title}</h3>
                </Link>
                <div data-testid="video-card-creator" className="text-xs text-[color:var(--alb-text-muted)]">{video.uploader_name}</div>
                <div className="flex items-center gap-3 text-[11px] text-[color:var(--alb-text-muted)] font-mono-tab">
                    <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {formatViews(video.views)}</span>
                    <button
                        type="button"
                        onClick={toggleLike}
                        disabled={likeBusy}
                        data-testid="video-card-like-button"
                        className={`inline-flex min-h-8 items-center gap-1 rounded-full px-2 hover:bg-white/10 ${liked ? 'text-[color:var(--alb-pink)]' : ''}`}
                        aria-label={`${liked ? 'Treure' : 'Donar'} M'agrada a ${video.title}`}
                    >
                        <Heart className={`h-3.5 w-3.5 ${liked ? 'fill-current' : ''}`} /> {formatViews(likes)}
                    </button>
                    <span>· {timeAgoCa(video.created_at)}</span>
                </div>
                {user && user.user_id !== video.uploader_id && (
                    <button
                        type="button"
                        onClick={toggleFollow}
                        disabled={followBusy}
                        data-testid="video-card-follow-button"
                        className={`mt-1.5 inline-flex min-h-7 items-center gap-1 rounded-full border px-2.5 text-[11px] font-mono-tab transition-colors ${following ? 'border-[rgba(245,233,216,0.16)] text-[color:var(--alb-text-muted)]' : 'border-[color:var(--alb-sun)] text-[color:var(--alb-sun)] hover:bg-[color:var(--alb-sun)] hover:text-[color:var(--alb-ink)]'}`}
                        aria-label={`${following ? 'Deixar de seguir' : 'Seguir'} ${video.uploader_name}`}
                    >
                        {following ? <UserMinus className="h-3 w-3" /> : <UserPlus className="h-3 w-3" />}
                        {following ? 'Seguint' : 'Seguir'}
                    </button>
                )}
            </div>
        </article>
    );
}
